import React from 'react';
import I18n from 'i18n-js';

import { gql } from '@reverbdotcom/commons/src/gql';
import {
  core_apimessages_Money as Money,
  SellCardDataFragment,
  CSPPriceRecommendationDataFragment,
} from '@reverbdotcom/commons/src/gql/graphql';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { RCProductCard } from '@reverbdotcom/cadence/components';

import { Paths } from '../shared/url_helpers';
import { PriceGuideRecommendationNudge, ProductFinishesNudge, TradeInNudge } from '../CardNudges';

interface IProps {
  active: boolean;
  csp: SellCardDataFragment & CSPPriceRecommendationDataFragment;
  loading: boolean;
  promotionalToken: string;
  useLegacySellFormRoute?: boolean;
  lowPriceRecommendation?: Money;
  highPriceRecommendation?: Money;
  hideProductFinishes?: boolean;
  hideTradeInNudge?: boolean;
}

export const SellCardFragment = gql(`
  fragment SellCardData on CSP {
    _id
    id
    title
    finishes
    image(input: { transform: "card_square" }) {
      _id
      source
    }
    slug
    brand {
      _id
      name
    }
    canonicalProductIds
    isTradeInEligible
  }
`);

// this query uses GOOD and MINT condition uuids to get price recommendations
export const CSPPriceRecommendationFragment = gql(`
  fragment CSPPriceRecommendationData on CSP {
    _id
    priceRecommendations(input: {
      conditionUuids: ["f7a3f48c-972a-44c6-b01a-0cd27488d3f6", "ac5b9c1e-dc78-466d-b0b3-7cf712967a48"],
      countryCode: $priceRecommendationCountryCode,
    }) {
      conditionUuid
      priceLow {
        amountCents
        amount
        currency
      }
      priceHigh {
        amountCents
        amount
        currency
      }
    }
  }
`);

export const SellCardWithCSPPriceRecommendationFragment = gql(`
  fragment SellCardWithCSPPriceRecommendationData on CSP {
    _id
    ...SellCardData
    ...CSPPriceRecommendationData
  }
`);

function getPrefilledListingUrl({ cspId, promotionalToken, useLegacySellFormRoute = false, isCSPTradeInEligible = false }: {
  cspId: string;
  promotionalToken: string;
  useLegacySellFormRoute: boolean;
  isCSPTradeInEligible: boolean;
}) {
  const hasPromoToken = !!promotionalToken;
  const promoToken = hasPromoToken && { promotional_token: promotionalToken };

  // the native iOS and Android apps still need to use the classic sell form routes to support app versions from earlier than September 2022
  if (useLegacySellFormRoute) {
    return Paths.legacyPrefilledSellForm.expand({
      seed_id: cspId,
      ...promoToken,
    });
  }

  return isCSPTradeInEligible ? Paths.listingOptions.expand({ seed_id: cspId, ...promoToken }) : Paths.prefilledListingFromCSP.expand({ seed_id: cspId, ...promoToken });
}

export function SellCard({
  csp,
  active = false,
  loading = false,
  promotionalToken,
  useLegacySellFormRoute = false,
  lowPriceRecommendation = null,
  highPriceRecommendation = null,
  hideProductFinishes = false,
  hideTradeInNudge = false,
}: IProps) {
  const isCSPTradeInEligible = csp.isTradeInEligible;
  const linkUrl = getPrefilledListingUrl({ cspId: csp.id, promotionalToken, useLegacySellFormRoute, isCSPTradeInEligible });

  if (loading) { return <RCProductCard.Skeleton />; }

  const { finishes } = csp;
  const finishesCount = finishes?.length ?? 0;

  return (
    <RCProductCard
      eyebrow={csp.brand?.name}
      title={csp.title?.replace(csp.brand?.name, '')}
      imageUrl={csp.image?.source}
      wrapper={{
        type: 'link',
        element: (children) => <CoreLink to={linkUrl}>{children}</CoreLink>,
      }}
    >
      {active && <div className="text-utility-350">{I18n.t('discovery.sellFlow.tiles.recommended')}</div>}
      <PriceGuideRecommendationNudge
        lowPriceRecommendation={lowPriceRecommendation}
        highPriceRecommendation={highPriceRecommendation}
        noLabel
      />
      {!hideProductFinishes && finishesCount > 1 && <ProductFinishesNudge finishes={finishes} />}
      { !hideTradeInNudge && <TradeInNudge isTradeInEligible={isCSPTradeInEligible} /> }
    </RCProductCard>
  );
}

export default SellCard;
